import { PackagePlus, ShoppingCart, CheckCircle2, Truck, PackageCheck, XCircle, Clock, AlertTriangle } from "lucide-react";

const ORDER = ["requested", "approved", "in_transit", "completed"];

// Aşama başına insan-okur zaman damgası (history + doc alanlarından)
function stepTime(detail, key) {
  const hist = detail.history || [];
  const find = (re) => {
    const hit = [...hist].reverse().find((h) => re.test(h.action || ""));
    return hit?.at || "";
  };
  let raw = "";
  if (key === "requested") raw = hist[0]?.at || detail.created_at || "";
  else if (key === "approved") {
    raw = find(/approv/i) || detail.from_approved?.at || detail.to_approved?.at
      || (["in_transit", "completed"].includes(detail.status) ? (hist[0]?.at || detail.created_at || "") : "");
  } else if (key === "in_transit") raw = detail.shipped_at || find(/ship/i);
  else if (key === "completed") raw = detail.received_at || find(/receiv/i);
  return raw ? String(raw).slice(0, 16).replace("T", " ") : "";
}

export default function TransferTimeline({ detail }) {
  const isPO = (detail.order_type || "transfer") === "purchase_order";
  const steps = [
    { key: "requested", label: isPO ? "Sipariş" : "Talep", icon: isPO ? ShoppingCart : PackagePlus },
    { key: "approved", label: "Onay", icon: CheckCircle2 },
    { key: "in_transit", label: isPO ? "Gönderildi" : "Sevk", icon: Truck },
    { key: "completed", label: "Mal Kabul", icon: PackageCheck },
  ];
  const status = detail.status || "requested";
  const terminal = status === "rejected" || status === "cancelled";
  const doneThrough = ORDER.indexOf(terminal ? "requested" : status); // completed → 3
  const stoppedAt = terminal
    ? ORDER.indexOf(detail.shipped_at ? "in_transit" : detail.from_approved || detail.to_approved || (detail.history || []).some((h) => /approv/i.test(h.action || "")) ? "approved" : "requested")
    : doneThrough;

  // Bekleme rozeti: şu an hangi aşamada, kimde ve kaç gündür bekliyor
  const waitSince = status === "requested"
    ? (detail.created_at || detail.history?.[0]?.at)
    : status === "approved"
      ? (detail.from_approved?.at || detail.to_approved?.at || [...(detail.history || [])].reverse().find((h) => /approv/i.test(h.action || ""))?.at || detail.created_at)
      : status === "in_transit" ? detail.shipped_at : null;
  const waitDays = waitSince ? Math.floor((Date.now() - new Date(waitSince)) / 86400000) : 0;
  const waitOverdue = waitDays >= 2;
  const chainStep = detail.approval_step || 0;
  const waitWho = status === "requested"
    ? (detail.approval_chain?.length ? `Onay: ${detail.approval_chain[chainStep]?.label || "Yönetici"}` : "Onay bekleniyor")
    : status === "approved" ? `Sevk bekleniyor · ${detail.from_location_name || ""}`
      : status === "in_transit" ? `Mal kabul bekleniyor · ${detail.to_location_name || ""}` : "";
  const showWait = !terminal && status !== "completed";

  return (
    <div data-testid="transfer-timeline" className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white px-4 py-4">
      <div className="flex items-start">
        {steps.map((st, i) => {
          const done = i <= (terminal ? stoppedAt : doneThrough);
          const current = !terminal && i === doneThrough + 1;
          const Icon = st.icon;
          const t = stepTime(detail, st.key);
          const circle = done
            ? "bg-brand-green text-white border-brand-green"
            : current
              ? "bg-amber-100 text-amber-700 border-amber-400 animate-pulse"
              : "bg-white text-slate-300 border-slate-200";
          const line = i < steps.length - 1 ? (i < (terminal ? stoppedAt : doneThrough) ? "bg-brand-green" : "bg-slate-200") : "";
          return (
            <div key={st.key} data-testid={`tl-step-${st.key}`} className="flex flex-1 flex-col items-center">
              <div className="flex w-full items-center">
                <div className="flex-1" />
                <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${circle}`}>
                  <Icon className="h-4 w-4" />
                </div>
                {i < steps.length - 1 && <div className={`h-1 flex-1 rounded-full ${line}`} />}
              </div>
              <p className={`mt-2 text-center text-[11px] font-semibold ${done ? "text-slate-700" : current ? "text-amber-600" : "text-slate-400"}`}>{st.label}</p>
              {t && <p data-testid={`tl-time-${st.key}`} className="text-center text-[10px] text-slate-400">{t}</p>}
            </div>
          );
        })}
      </div>
      {showWait && (
        <div data-testid="tl-waiting" className={`mt-3 flex flex-wrap items-center justify-center gap-1.5 rounded-lg py-1.5 px-3 text-xs font-semibold ${waitOverdue ? "bg-red-50 text-red-600" : "bg-amber-50 text-amber-700"}`}>
          {waitOverdue ? <AlertTriangle className="h-3.5 w-3.5" /> : <Clock className="h-3.5 w-3.5" />}
          <span data-testid="tl-waiting-days">{waitDays <= 0 ? "Bugün bu aşamada" : `${waitDays} gündür bekliyor`}</span>
          <span className="text-slate-300">·</span>
          <span className="font-medium">{waitWho}</span>
        </div>
      )}
      {terminal && (
        <div data-testid="tl-terminal" className={`mt-3 flex items-center justify-center gap-1.5 rounded-lg py-1.5 text-xs font-semibold ${status === "rejected" ? "bg-red-50 text-red-600" : "bg-slate-100 text-slate-500"}`}>
          <XCircle className="h-3.5 w-3.5" />
          {status === "rejected" ? "Bu talep reddedildi" : "Bu talep iptal edildi"}
        </div>
      )}
    </div>
  );
}
